/**
 * Action Registry
 *
 * Central registry of all object actions. Provides functions to get
 * actions applicable to specific object types and execute actions.
 */

import type {
  ObjectActionDefinition,
  ObjectActionContext,
  ObjectActionMenuItem,
  GroupedObjectActions,
  SchemaObjectType,
} from './types';
import { queryActions } from './actions/queryActions';
import { scriptActions } from './actions/scriptActions';
import { designActions } from './actions/designActions';
import { infoActions } from './actions/infoActions';
import { packageActions } from './actions/packageActions';

/**
 * All registered actions
 */
const allActions: ObjectActionDefinition[] = [
  ...queryActions,
  ...scriptActions,
  ...designActions,
  ...infoActions,
  ...packageActions,
];

/**
 * Get all actions that apply to a specific object type
 */
export function getActionsForObject(objectType: SchemaObjectType): ObjectActionDefinition[] {
  return allActions.filter((action) => action.appliesTo.includes(objectType));
}

/**
 * Get actions formatted as menu items for a specific object type and context
 */
export function getMenuItemsForObject(
  objectType: SchemaObjectType,
  context: ObjectActionContext,
): ObjectActionMenuItem[] {
  const actions = getActionsForObject(objectType);

  return actions.map((action) => ({
    id: action.id,
    label: action.label,
    icon: action.icon,
    shortcut: action.shortcut,
    category: action.category,
    isPrimary: action.isPrimary,
    isEnabled: action.isEnabled ? action.isEnabled(context) : true,
  }));
}

/**
 * Get actions grouped by category for menu rendering
 */
export function getGroupedActionsForObject(
  objectType: SchemaObjectType,
  context: ObjectActionContext,
): GroupedObjectActions {
  const menuItems = getMenuItemsForObject(objectType, context);

  return {
    query: menuItems.filter((item) => item.category === 'query'),
    script: menuItems.filter((item) => item.category === 'script'),
    design: menuItems.filter((item) => item.category === 'design'),
    info: menuItems.filter((item) => item.category === 'info'),
  };
}

/**
 * Get primary actions (shown as buttons) for a specific object type
 */
export function getPrimaryActionsForObject(
  objectType: SchemaObjectType,
  context: ObjectActionContext,
): ObjectActionMenuItem[] {
  return getMenuItemsForObject(objectType, context).filter((item) => item.isPrimary);
}

/**
 * Execute an action by ID with the given context
 */
export async function executeAction(
  actionId: string,
  context: ObjectActionContext,
): Promise<void> {
  const action = allActions.find((a) => a.id === actionId);

  if (!action) {
    console.warn(`[ActionRegistry] Unknown action: ${actionId}`);
    return;
  }

  if (action.isEnabled && !action.isEnabled(context)) {
    console.warn(`[ActionRegistry] Action ${actionId} is not enabled for this context`);
    return;
  }

  if (!action.appliesTo.includes(context.objectType)) {
    console.warn(`[ActionRegistry] Action ${actionId} does not apply to ${context.objectType}`);
    return;
  }

  await action.execute(context);
}

/**
 * Get an action definition by ID
 */
export function getActionById(actionId: string): ObjectActionDefinition | undefined {
  return allActions.find((a) => a.id === actionId);
}

/**
 * Get all registered actions
 */
export function getAllActions(): ObjectActionDefinition[] {
  return [...allActions];
}

/**
 * Get count of actions by object type
 */
export function getActionCountByType(objectType: SchemaObjectType): number {
  return getActionsForObject(objectType).length;
}
